export type PolicyMode = 'all_enabled' | 'allowlist' | 'disabled';
export interface PolicyRoute {
  connector_account_id: string;
  access_code: string;
}
export interface PolicyRouteOption extends PolicyRoute {
  display_name: string;
}
export interface DestinationPolicy {
  source: 'ubikeishon';
  place_id: string;
  display_name?: string;
  mode: PolicyMode | 'inherit' | 'extend';
  routes: PolicyRoute[];
}
export interface SearchPolicy {
  revision: number;
  default_mode: PolicyMode;
  default_routes: PolicyRoute[];
  destination_overrides: DestinationPolicy[];
}
export function routeKey(route: PolicyRoute) {
  return JSON.stringify([route.connector_account_id, route.access_code]);
}
export function toggleRoute(routes: PolicyRoute[], option: PolicyRoute, selected: boolean) {
  const rest = routes.filter((route) => routeKey(route) !== routeKey(option));
  return selected
    ? [...rest, { connector_account_id: option.connector_account_id, access_code: option.access_code }]
    : rest;
}
export function policyPayload(policy: SearchPolicy, reason: string) {
  return {
    expected_revision: policy.revision,
    reason: reason.trim(),
    default_mode: policy.default_mode,
    default_routes: policy.default_mode === 'allowlist' ? policy.default_routes : [],
    destination_overrides: policy.destination_overrides.map((row) => ({
      ...row,
      routes: row.mode === 'allowlist' || row.mode === 'extend' ? row.routes : [],
    })),
  };
}
export function policyValid(policy: SearchPolicy, reason: string) {
  return Boolean(
    reason.trim() &&
      reason.trim().length <= 1000 &&
      policy.destination_overrides.length <= 200 &&
      policy.destination_overrides.every((row) => /^plc_.+/.test(row.place_id)) &&
      new Set(policy.destination_overrides.map((row) => row.place_id)).size === policy.destination_overrides.length,
  );
}

// Preview uses the same active inventory boundary as server-side routing.
export function effectivePolicyRoutes(
  policy: SearchPolicy,
  options: PolicyRouteOption[],
  destination?: DestinationPolicy,
) {
  const defaults = options.filter(
    (option) =>
      policy.default_mode === 'all_enabled' ||
      (policy.default_mode === 'allowlist' &&
        policy.default_routes.some((route) => routeKey(route) === routeKey(option))),
  );
  if (!destination || destination.mode === 'inherit') return defaults;
  if (destination.mode === 'disabled') return [];
  if (destination.mode === 'all_enabled') return options;
  const selected = new Set(destination.routes.map(routeKey));
  if (destination.mode === 'extend') defaults.forEach((route) => selected.add(routeKey(route)));
  return options.filter((route) => selected.has(routeKey(route)));
}
